import React, { useState, useEffect } from "react";

const METRICS = [
  "cagr_3y","cagr_5y","rolling_return_3y_avg","sharpe_ratio","sortino_ratio","calmar_ratio",
  "max_drawdown","std_dev_annual","var_95","alpha","beta","information_ratio",
  "beat_category_pct","up_capture_ratio","down_capture_ratio","tracking_error","beta_vs_index","expense_ratio",
];
const GROUPS = ["G1","G1P","G2","G2M","G3A","G3B","G4","G5"];
const FILTER_SECTIONS = ["GLOBAL","EXCLUDE_KEYWORDS","AUM_THRESHOLDS"];
const SIP_PROFILES = ["Conservative Equity Growth Plan","Moderate Equity Growth Plan","Aggressive Equity Growth Plan"];

export default function ConfigPanel({ token, API }) {
  const headers = { Authorization: "Bearer " + token };
  const [modalBatch, setModalBatch] = useState(null);

  return (
    <div style={s.wrap}>
      <FiltersSection headers={headers} API={API} onSaved={setModalBatch} />
      <WeightsSection headers={headers} API={API} onSaved={setModalBatch} />
      <SipSection headers={headers} API={API} onSaved={setModalBatch} />
      {modalBatch && (
        <PostSaveModal batch={modalBatch} headers={headers} API={API} onClose={() => setModalBatch(null)} />
      )}
    </div>
  );
}

function ChangeHistory({ batch, headers, API }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (!open && !loaded) {
      setLoading(true);
      try {
        const res = await fetch(API + "/api/admin/config/history/" + batch, { headers });
        const data = await res.json();
        setRows(data.history || []);
      } catch { /* ignore */ }
      setLoaded(true);
      setLoading(false);
    }
    setOpen(!open);
  };

  return (
    <div style={s.historyWrap}>
      <button style={s.historyToggle} onClick={toggle}>
        {open ? "▲ Hide Change History" : "▼ View Change History"}
      </button>
      {open && (
        loading ? <p style={s.empty}>Loading...</p> :
        rows.length === 0 ? <p style={s.empty}>No history yet.</p> : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead><tr style={s.thead}>
                <th style={s.th}>Timestamp</th><th style={s.th}>Section</th>
                <th style={s.th}>Parameter</th><th style={s.th}>Value</th>
                <th style={s.th}>Status</th><th style={s.th}>Notes</th>
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={s.tr}>
                    <td style={s.td}>{r.changed_at ? r.changed_at.slice(0,16) : "—"}</td>
                    <td style={s.td}>{r.section}</td>
                    <td style={s.td}>{r.parameter}</td>
                    <td style={s.td}>{r.value_num ?? r.value_text ?? "—"}</td>
                    <td style={s.td}>
                      <span style={r.is_active ? s.successText : s.errorText}>
                        {r.is_active ? "Active" : "Replaced"}
                      </span>
                    </td>
                    <td style={s.td}>{r.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function PostSaveModal({ batch, headers, API, onClose }) {
  const [msg, setMsg] = useState("");
  const [running, setRunning] = useState(false);

  const PHASES = {
    phase1: { label: "Phase 1", endpoint: "/api/admin/action/run-phase1" },
    phase2: { label: "Phase 2", endpoint: "/api/admin/action/run-phase2" },
    phase3: { label: "Phase 3", endpoint: "/api/admin/action/run-phase3" },
  };

  const runSequential = async (phases) => {
    setRunning(true);
    for (let i = 0; i < phases.length; i++) {
      const phase = phases[i];
      setMsg(m => m + (m ? "\n" : "") + `⏳ Starting ${phase.label}...`);
      try {
        const res = await fetch(API + phase.endpoint, { method: "POST", headers });
        const data = await res.json();
        setMsg(m => m + "\n" + (data.status === "started" ? `✅ ${data.message}` : `❌ ${data.message || "Failed"}`));
      } catch (e) {
        setMsg(m => m + `\n❌ ${e.message}`);
      }
      if (i < phases.length - 1) await new Promise(r => setTimeout(r, 2000));
    }
    setRunning(false);
  };

  let body = "";
  let actions = null;
  if (batch === "FILTERS") {
    body = "Filters updated. Run Phase 1 → 2 → 3 now to apply changes?";
    actions = (
      <>
        <button style={s.modalBtnPrimary} disabled={running}
          onClick={() => runSequential([PHASES.phase1, PHASES.phase2, PHASES.phase3])}>
          {running ? "Running..." : "Run Phase 1+2+3"}
        </button>
        <button style={s.modalBtnCancel} onClick={onClose}>Later (schedule will pick up)</button>
      </>
    );
  } else if (batch === "WEIGHTS") {
    body = "Weights updated. Run Phase 2 → 3 now to apply changes? (Phase 1 not needed)";
    actions = (
      <>
        <button style={s.modalBtnPrimary} disabled={running}
          onClick={() => runSequential([PHASES.phase2, PHASES.phase3])}>
          {running ? "Running..." : "Run Phase 2+3"}
        </button>
        <button style={s.modalBtnCancel} onClick={onClose}>Later</button>
      </>
    );
  } else {
    body = "SIP Profile updated. No phase re-run needed — changes apply immediately on next report generation.";
    actions = <button style={s.modalBtnPrimary} onClick={onClose}>OK</button>;
  }

  return (
    <div style={s.modalOverlay}>
      <div style={s.modalBox}>
        <h3 style={s.modalTitle}>Config Saved</h3>
        <p style={s.modalBody}>{body}</p>
        {msg && <pre style={s.modalMsg}>{msg}</pre>}
        <div style={s.modalActions}>{actions}</div>
      </div>
    </div>
  );
}

function FiltersSection({ headers, API, onSaved }) {
  const [open, setOpen] = useState(true);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({ value: "", notes: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ section: "GLOBAL", parameter: "", value: "", notes: "" });

  const isNumeric = (section) => section !== "EXCLUDE_KEYWORDS";

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await fetch(API + "/api/admin/config/FILTERS", { headers });
      const data = await res.json();
      setRows(data.rows || []);
    } catch { setMsg("✖ Failed to load filters"); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRows(); }, []);

  const startEdit = (row) => {
    setEditKey(row.section + ":" + row.parameter);
    setEditVals({
      value: isNumeric(row.section) ? (row.value_num ?? "") : (row.value_text ?? ""),
      notes: row.notes || "",
    });
  };

  const saveRow = async (row) => {
    const body = { section: row.section, parameter: row.parameter, notes: editVals.notes };
    if (isNumeric(row.section)) body.value_num = editVals.value === "" ? null : Number(editVals.value);
    else body.value_text = editVals.value;
    try {
      const res = await fetch(API + "/api/admin/config/FILTERS", {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Save failed");
      setMsg("✔ Saved"); setEditKey(null);
      await fetchRows();
      onSaved("FILTERS");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete ${row.parameter} from ${row.section}? This takes effect on next Phase 1 run.`)) return;
    try {
      const res = await fetch(
        API + `/api/admin/config/FILTERS/${encodeURIComponent(row.section)}/${encodeURIComponent(row.parameter)}`,
        { method: "DELETE", headers }
      );
      if (!res.ok) throw new Error("Delete failed");
      setMsg("✔ Deleted");
      await fetchRows();
      onSaved("FILTERS");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  const submitAdd = async () => {
    if (!addForm.parameter || addForm.value === "") { setMsg("✖ Parameter and Value are required"); return; }
    const body = { section: addForm.section, parameter: addForm.parameter, notes: addForm.notes };
    if (isNumeric(addForm.section)) body.value_num = Number(addForm.value);
    else body.value_text = addForm.value;
    try {
      const res = await fetch(API + "/api/admin/config/FILTERS", {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Add failed");
      setMsg("✔ Added"); setShowAdd(false);
      setAddForm({ section: "GLOBAL", parameter: "", value: "", notes: "" });
      await fetchRows();
      onSaved("FILTERS");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  const grouped = FILTER_SECTIONS
    .map(sec => ({ section: sec, items: rows.filter(r => r.section === sec) }))
    .concat(
      [...new Set(rows.map(r => r.section))]
        .filter(sec => !FILTER_SECTIONS.includes(sec))
        .map(sec => ({ section: sec, items: rows.filter(r => r.section === sec) }))
    );

  return (
    <div style={s.card}>
      <div style={s.sectionHdr} onClick={() => setOpen(!open)}>
        <span>{open ? "▼" : "▶"} Phase Filters</span>
      </div>
      {open && (
        <div style={s.cardBody}>
          {msg && <div style={msg.startsWith("✔") ? s.successText : s.errorText}>{msg}</div>}
          {loading ? <p style={s.empty}>Loading...</p> : (
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead><tr style={s.thead}>
                  <th style={s.th}>Section</th><th style={s.th}>Parameter</th>
                  <th style={s.th}>Value</th><th style={s.th}>Notes</th><th style={s.th}>Actions</th>
                </tr></thead>
                <tbody>
                  {grouped.map(g => g.items.length === 0 ? null : (
                    <React.Fragment key={g.section}>
                      <tr style={s.groupRow}><td style={s.groupCell} colSpan={5}>{g.section}</td></tr>
                      {g.items.map(row => {
                        const key = row.section + ":" + row.parameter;
                        const editing = editKey === key;
                        return (
                          <tr key={row.id} style={s.tr}>
                            <td style={s.td}>{row.section}</td>
                            <td style={s.td}>{row.parameter}</td>
                            <td style={s.td}>
                              {editing ? (
                                <input style={s.inlineInput} type={isNumeric(row.section) ? "number" : "text"}
                                  value={editVals.value} onChange={e => setEditVals({...editVals, value:e.target.value})} />
                              ) : (isNumeric(row.section) ? row.value_num : row.value_text)}
                            </td>
                            <td style={s.td}>
                              {editing ? (
                                <input style={s.inlineInput} value={editVals.notes}
                                  onChange={e => setEditVals({...editVals, notes:e.target.value})} />
                              ) : (row.notes || "—")}
                            </td>
                            <td style={s.td}>
                              {editing ? (
                                <>
                                  <button style={s.btnSave} onClick={() => saveRow(row)}>Save Row</button>
                                  <button style={s.btnCancelSm} onClick={() => setEditKey(null)}>Cancel</button>
                                </>
                              ) : (
                                <>
                                  <button style={s.btnEdit} onClick={() => startEdit(row)}>Edit</button>
                                  <button style={s.btnDel} onClick={() => deleteRow(row)}>Delete</button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!showAdd ? (
            <button style={s.btnAdd} onClick={() => setShowAdd(true)}>➕ Add Row</button>
          ) : (
            <div style={s.addForm}>
              <select style={s.input} value={addForm.section} onChange={e => setAddForm({...addForm, section:e.target.value})}>
                {FILTER_SECTIONS.map(sec => <option key={sec} value={sec}>{sec}</option>)}
              </select>
              <input style={s.input} placeholder="Parameter" value={addForm.parameter}
                onChange={e => setAddForm({...addForm, parameter:e.target.value})} />
              <input style={s.input} type={isNumeric(addForm.section) ? "number" : "text"} placeholder="Value"
                value={addForm.value} onChange={e => setAddForm({...addForm, value:e.target.value})} />
              <input style={s.input} placeholder="Notes" value={addForm.notes}
                onChange={e => setAddForm({...addForm, notes:e.target.value})} />
              <button style={s.btnSave} onClick={submitAdd}>Submit</button>
              <button style={s.btnCancelSm} onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          )}
          <ChangeHistory batch="FILTERS" headers={headers} API={API} />
        </div>
      )}
    </div>
  );
}

function WeightsSection({ headers, API, onSaved }) {
  const [open, setOpen] = useState(true);
  const [grid, setGrid] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [groupMsg, setGroupMsg] = useState({});
  const [saving, setSaving] = useState({});

  const fetchWeights = async () => {
    setLoading(true);
    try {
      const res = await fetch(API + "/api/admin/config/WEIGHTS", { headers });
      const data = await res.json();
      const g = {};
      GROUPS.forEach(grp => { g[grp] = {}; METRICS.forEach(m => { g[grp][m] = 0; }); });
      (data.rows || []).forEach(r => {
        if (!g[r.section]) g[r.section] = {};
        g[r.section][r.parameter] = r.value_num ?? 0;
      });
      setGrid(g);
    } catch { setMsg("✖ Failed to load weights"); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchWeights(); }, []);

  const setCell = (group, metric, val) => {
    setGrid(g => ({ ...g, [group]: { ...g[group], [metric]: val } }));
  };

  const colTotal = (group) => METRICS.reduce((sum, m) => sum + (Number(grid[group]?.[m]) || 0), 0);

  const saveGroup = async (group) => {
    const total = colTotal(group);
    if (total !== 100) {
      setGroupMsg(m => ({...m, [group]: "⚠ Must sum to 100 (currently " + total + ")"}));
      return;
    }
    setSaving(s => ({...s, [group]: true}));
    setGroupMsg(m => ({...m, [group]: ""}));
    try {
      for (const metric of METRICS) {
        const res = await fetch(API + "/api/admin/config/WEIGHTS", {
          method: "POST", headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({ section: group, parameter: metric, value_num: Number(grid[group][metric]) || 0 }),
        });
        if (!res.ok) throw new Error("Save failed for " + metric);
      }
      setGroupMsg(m => ({...m, [group]: "✔ Saved"}));
      onSaved("WEIGHTS");
    } catch (e) {
      setGroupMsg(m => ({...m, [group]: "✖ " + e.message}));
    }
    setSaving(s => ({...s, [group]: false}));
  };

  return (
    <div style={s.card}>
      <div style={s.sectionHdr} onClick={() => setOpen(!open)}>
        <span>{open ? "▼" : "▶"} Scoring Weights</span>
      </div>
      {open && (
        <div style={s.cardBody}>
          {msg && <div style={s.errorText}>{msg}</div>}
          {loading ? <p style={s.empty}>Loading...</p> : (
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr style={s.thead}>
                    <th style={s.th}>Metric</th>
                    {GROUPS.map(g => <th key={g} style={s.th}>{g}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map(metric => (
                    <tr key={metric} style={s.tr}>
                      <td style={{...s.td, fontWeight:"600"}}>{metric}</td>
                      {GROUPS.map(group => (
                        <td key={group} style={s.td}>
                          <input style={s.gridInput} type="number" min="0" max="100" step="1"
                            value={grid[group]?.[metric] ?? 0}
                            onChange={e => setCell(group, metric, e.target.value)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr style={s.totalRow}>
                    <td style={{...s.td, fontWeight:"700"}}>Total</td>
                    {GROUPS.map(group => {
                      const total = colTotal(group);
                      const ok = total === 100;
                      return (
                        <td key={group} style={s.td}>
                          <span style={ok ? s.successText : s.errorText}>
                            {total}{!ok && " ⚠ Must sum to 100"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td style={s.td}></td>
                    {GROUPS.map(group => (
                      <td key={group} style={s.td}>
                        <button style={s.btnSave} disabled={saving[group]} onClick={() => saveGroup(group)}>
                          {saving[group] ? "Saving..." : "Save Group"}
                        </button>
                        {groupMsg[group] && (
                          <div style={groupMsg[group].startsWith("✔") ? s.successText : s.errorText}>
                            {groupMsg[group]}
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <ChangeHistory batch="WEIGHTS" headers={headers} API={API} />
        </div>
      )}
    </div>
  );
}

function SipSection({ headers, API, onSaved }) {
  const [open, setOpen] = useState(true);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [sipTab, setSipTab] = useState("Conservative Equity Growth Plan");
  const [descDraft, setDescDraft] = useState("");
  const [editKey, setEditKey] = useState(null);
  const [editVals, setEditVals] = useState({ label: "", alloc: "", logic: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ code: "", label: "", alloc: "", logic: "" });

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await fetch(API + "/api/admin/config/SIP", { headers });
      const data = await res.json();
      const p = {};
      SIP_PROFILES.forEach(name => { p[name] = { desc: "", cats: [] }; });
      (data.rows || []).forEach(r => {
        if (!p[r.section]) p[r.section] = { desc: "", cats: [] };
        if (r.parameter === "desc") {
          p[r.section].desc = r.value_text || "";
        } else {
          const parts = (r.value_text || "").split("|");
          p[r.section].cats.push({
            code: r.parameter,
            label: parts[0] || r.parameter,
            logic: parts[1] || "",
            alloc: r.value_num ?? 0,
          });
        }
      });
      setProfiles(p);
    } catch { setMsg("✖ Failed to load SIP profiles"); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProfiles(); }, []);
  useEffect(() => { setDescDraft((profiles[sipTab] || {}).desc || ""); }, [sipTab, profiles]);

  const current = profiles[sipTab] || { desc: "", cats: [] };
  const REFERENCE_CODES = ["base_effective_equity", "stress_effective_equity"];
  const realCats = current.cats.filter(c => !REFERENCE_CODES.includes(c.code));
  const refCats = current.cats.filter(c => REFERENCE_CODES.includes(c.code));
  const allocTotal = realCats.reduce((sum, c) => sum + (Number(c.alloc) || 0), 0);

  const saveDesc = async () => {
    try {
      const res = await fetch(API + "/api/admin/config/SIP", {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ section: sipTab, parameter: "desc", value_text: descDraft }),
      });
      if (!res.ok) throw new Error("Save failed");
      setMsg("✔ Description saved");
      await fetchProfiles();
      onSaved("SIP");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  const renderCatRow = (cat, rowStyle) => {
    const editing = editKey === cat.code;
    return (
      <tr key={cat.code} style={rowStyle}>
        <td style={s.td}>{cat.code}</td>
        <td style={s.td}>
          {editing ? <input style={s.inlineInput} value={editVals.label}
            onChange={e => setEditVals({...editVals, label:e.target.value})} /> : cat.label}
        </td>
        <td style={s.td}>
          {editing ? <input style={s.inlineInput} type="number" value={editVals.alloc}
            onChange={e => setEditVals({...editVals, alloc:e.target.value})} /> : cat.alloc + "%"}
        </td>
        <td style={s.td}>
          {editing ? <input style={{...s.inlineInput, width:"100%"}} value={editVals.logic}
            onChange={e => setEditVals({...editVals, logic:e.target.value})} /> : cat.logic}
        </td>
        <td style={s.td}>
          {editing ? (
            <>
              <button style={s.btnSave} onClick={() => saveCat(cat)}>Save Row</button>
              <button style={s.btnCancelSm} onClick={() => setEditKey(null)}>Cancel</button>
            </>
          ) : (
            <>
              <button style={s.btnEdit} onClick={() => startEdit(cat)}>Edit</button>
              <button style={s.btnDel} onClick={() => deleteCat(cat)}>Delete</button>
            </>
          )}
        </td>
      </tr>
    );
  };

  const startEdit = (cat) => {
    setEditKey(cat.code);
    setEditVals({ label: cat.label, alloc: cat.alloc, logic: cat.logic });
  };

  const saveCat = async (cat) => {
    try {
      const res = await fetch(API + "/api/admin/config/SIP", {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          section: sipTab, parameter: cat.code,
          value_num: Number(editVals.alloc) || 0,
          value_text: editVals.label + "|" + editVals.logic,
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      setMsg("✔ Saved"); setEditKey(null);
      await fetchProfiles();
      onSaved("SIP");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  const deleteCat = async (cat) => {
    if (!window.confirm(`Delete ${cat.code} from ${sipTab}?`)) return;
    try {
      const res = await fetch(
        API + `/api/admin/config/SIP/${encodeURIComponent(sipTab)}/${encodeURIComponent(cat.code)}`,
        { method: "DELETE", headers }
      );
      if (!res.ok) throw new Error("Delete failed");
      setMsg("✔ Deleted");
      await fetchProfiles();
      onSaved("SIP");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  const submitAdd = async () => {
    if (!addForm.code || addForm.alloc === "") { setMsg("✖ Category Code and Allocation % are required"); return; }
    try {
      const res = await fetch(API + "/api/admin/config/SIP", {
        method: "POST", headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          section: sipTab, parameter: addForm.code,
          value_num: Number(addForm.alloc) || 0,
          value_text: addForm.label + "|" + addForm.logic,
        }),
      });
      if (!res.ok) throw new Error("Add failed");
      setMsg("✔ Added"); setShowAdd(false);
      setAddForm({ code: "", label: "", alloc: "", logic: "" });
      await fetchProfiles();
      onSaved("SIP");
    } catch (e) { setMsg("✖ " + e.message); }
  };

  return (
    <div style={s.card}>
      <div style={s.sectionHdr} onClick={() => setOpen(!open)}>
        <span>{open ? "▼" : "▶"} SIP Profiles</span>
      </div>
      {open && (
        <div style={s.cardBody}>
          {msg && <div style={msg.startsWith("✔") ? s.successText : s.errorText}>{msg}</div>}
          <div style={s.subTabs}>
            {SIP_PROFILES.map(name => (
              <button key={name} onClick={() => setSipTab(name)}
                style={sipTab === name ? {...s.subTab, ...s.subTabActive} : s.subTab}>{name}</button>
            ))}
          </div>
          {loading ? <p style={s.empty}>Loading...</p> : (
            <>
              <div style={s.formGroup}>
                <label style={s.label}>Profile Description</label>
                <textarea style={s.textarea} rows={4} value={descDraft} onChange={e => setDescDraft(e.target.value)} />
                <button style={{...s.btnSave, marginTop:"8px", alignSelf:"flex-start"}} onClick={saveDesc}>Save Description</button>
              </div>
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead><tr style={s.thead}>
                    <th style={s.th}>Category Code</th><th style={s.th}>Label</th>
                    <th style={s.th}>Allocation %</th><th style={s.th}>Investment Logic</th>
                    <th style={s.th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {realCats.map(cat => renderCatRow(cat, s.tr))}
                    <tr style={s.totalRow}>
                      <td style={s.td}></td><td style={{...s.td, fontWeight:"700"}}>Total</td>
                      <td style={s.td}>
                        <span style={allocTotal === 100 ? s.successText : s.errorText}>
                          {allocTotal}%{allocTotal !== 100 && " ⚠ Must sum to 100"}
                        </span>
                      </td>
                      <td style={s.td}></td><td style={s.td}></td>
                    </tr>
                    {refCats.length > 0 && (
                      <>
                        <tr style={s.groupRow}>
                          <td style={s.groupCell} colSpan={5}>Reference values (derived, excluded from allocation total)</td>
                        </tr>
                        {refCats.map(cat => renderCatRow(cat, s.refRow))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {!showAdd ? (
                <button style={s.btnAdd} onClick={() => setShowAdd(true)}>➕ Add Category</button>
              ) : (
                <div style={s.addForm}>
                  <input style={s.input} placeholder="Category Code" value={addForm.code}
                    onChange={e => setAddForm({...addForm, code:e.target.value})} />
                  <input style={s.input} placeholder="Label" value={addForm.label}
                    onChange={e => setAddForm({...addForm, label:e.target.value})} />
                  <input style={s.input} type="number" placeholder="Allocation %" value={addForm.alloc}
                    onChange={e => setAddForm({...addForm, alloc:e.target.value})} />
                  <input style={s.input} placeholder="Investment Logic" value={addForm.logic}
                    onChange={e => setAddForm({...addForm, logic:e.target.value})} />
                  <button style={s.btnSave} onClick={submitAdd}>Submit</button>
                  <button style={s.btnCancelSm} onClick={() => setShowAdd(false)}>Cancel</button>
                </div>
              )}
            </>
          )}
          <ChangeHistory batch="SIP" headers={headers} API={API} />
        </div>
      )}
    </div>
  );
}

const s = {
  wrap:       { display:"flex", flexDirection:"column", gap:"20px" },
  card:       { background:"#fff", borderRadius:"12px", padding:"24px", boxShadow:"0 2px 12px rgba(0,0,0,0.08)" },
  sectionHdr: { background:"#1565C0", color:"#fff", padding:"12px 16px", borderRadius:"6px", fontWeight:"700", cursor:"pointer", fontSize:"15px" },
  cardBody:   { marginTop:"16px" },
  empty:      { color:"#999", textAlign:"center", padding:"20px" },
  tableWrap:  { overflowX:"auto" },
  table:      { width:"100%", borderCollapse:"collapse", fontSize:"13px" },
  thead:      { background:"#1565C0" },
  th:         { color:"#fff", padding:"10px 12px", textAlign:"left", whiteSpace:"nowrap" },
  tr:         { borderBottom:"1px solid #f0f0f0" },
  td:         { padding:"8px 12px", verticalAlign:"middle" },
  groupRow:   { background:"#E3F0FF" },
  groupCell:  { padding:"6px 12px", color:"#1565C0", fontWeight:"700", fontSize:"12px" },
  totalRow:   { background:"#FAFAFA", borderTop:"2px solid #1565C0" },
  refRow:     { borderBottom:"1px solid #f0f0f0", background:"#FAFBFC", fontStyle:"italic", color:"#666" },
  inlineInput:{ border:"1.5px solid #ddd", borderRadius:"8px", padding:"6px 10px", fontSize:"13px", width:"120px" },
  gridInput:  { border:"1.5px solid #ddd", borderRadius:"6px", padding:"6px 8px", fontSize:"13px", width:"56px", textAlign:"center" },
  input:      { border:"1.5px solid #ddd", borderRadius:"8px", padding:"8px 12px", fontSize:"14px" },
  textarea:   { border:"1.5px solid #ddd", borderRadius:"8px", padding:"8px 12px", fontSize:"14px", width:"100%", fontFamily:"inherit", resize:"vertical" },
  formGroup:  { display:"flex", flexDirection:"column", gap:"6px", marginBottom:"20px" },
  label:      { fontWeight:"600", fontSize:"13px", color:"#444" },
  addForm:    { display:"flex", flexWrap:"wrap", gap:"8px", alignItems:"center", marginTop:"12px" },
  btnSave:    { background:"#1565C0", color:"#fff", border:"none", borderRadius:"6px", padding:"6px 12px", cursor:"pointer", fontSize:"12px", fontWeight:"600", marginRight:"4px" },
  btnEdit:    { background:"#1565C0", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px", marginRight:"4px" },
  btnDel:     { background:"#CC0000", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px" },
  btnCancelSm:{ background:"#546E7A", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px" },
  btnAdd:     { background:"#1565C0", color:"#fff", border:"none", borderRadius:"8px", padding:"10px 20px", cursor:"pointer", fontSize:"13px", fontWeight:"600", marginTop:"12px" },
  errorText:  { color:"#CC0000", fontSize:"13px", fontWeight:"600" },
  successText:{ color:"#2e7d32", fontSize:"13px", fontWeight:"600" },
  subTabs:    { display:"flex", gap:"8px", marginBottom:"16px" },
  subTab:     { padding:"8px 16px", border:"1.5px solid #1565C0", borderRadius:"8px", background:"#fff", color:"#1565C0", cursor:"pointer", fontWeight:"600", fontSize:"13px" },
  subTabActive:{ background:"#1565C0", color:"#fff" },
  historyWrap:{ marginTop:"20px" },
  historyToggle:{ background:"none", border:"1.5px solid #1565C0", color:"#1565C0", borderRadius:"8px", padding:"8px 16px", cursor:"pointer", fontSize:"13px", fontWeight:"600" },
  modalOverlay:{ position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", zIndex:1000 },
  modalBox:   { background:"#fff", borderRadius:"12px", padding:"32px", maxWidth:"480px", margin:"auto", marginTop:"15%" },
  modalTitle: { color:"#1565C0", margin:"0 0 12px 0" },
  modalBody:  { color:"#333", fontSize:"14px", lineHeight:"1.5" },
  modalMsg:   { background:"#F5F5F5", borderRadius:"6px", padding:"10px", fontSize:"12px", whiteSpace:"pre-wrap", maxHeight:"160px", overflowY:"auto" },
  modalActions:{ display:"flex", gap:"12px", marginTop:"20px", flexWrap:"wrap" },
  modalBtnPrimary:{ background:"#1565C0", color:"#fff", border:"none", borderRadius:"8px", padding:"10px 20px", cursor:"pointer", fontWeight:"600", fontSize:"14px" },
  modalBtnCancel:{ background:"#546E7A", color:"#fff", border:"none", borderRadius:"8px", padding:"10px 20px", cursor:"pointer", fontWeight:"600", fontSize:"14px" },
};
